import { IPaginatedParams, PageInfo } from '../../share-types'
import { TagActivityCategoryEnums } from '../tags-container/constant'
import {
  CorporateCreditsBufferTypeEnums,
  CorporateCreditsHistoryTypeEnum,
  CorporateMemberCreditsHistoryReferenceTypeEnums,
  CorporateMemberCreditsHistoryTypeEnums,
} from './constant'
import dayjs from 'dayjs'

export type ICorporatesFilterParams = {
  name?: string
  isActive?: boolean
}

export type IPaginatedCorporatesParams = IPaginatedParams & ICorporatesFilterParams

export type ICorporate = {
  id: number
  name: string
  credits: number
  price: number | null
  allowShowCreditsPool: boolean
  createdAt: string
  updatedAt: string
  isActive: boolean
  creditsValidityPeriodInDays: number
  tagIds: number[] | null
  allowActivityCategories: TagActivityCategoryEnums[] | null
  disallowActivityList: number[] | null
  maxCreditsUsageThreshold: number | null
  enabledMixPayment: boolean
  studioIds: number[] | null
  disallowStudioIds: number[] | null
  disallowCityList: number[] | null
  unusedDistributedCredits: number
  unusedDistributedCreditsThreshold: number
  isTrial: boolean
  principal: string | null
  cooperationMode: string | null
  contractDeadline: dayjs.Dayjs | null
  distributionFrequency: string | null
  nextDistributionTime: dayjs.Dayjs | null
  distributionRuleRemark: string | null
  activeMemberNumber: number
  usageRate: number
  enableReclaimCancelBooking: boolean
}

export type ICorporateReport = {
  distributedCredits: number
  memberUsedCredits: number
  memberUnusedCredits: number
  activatedMemberCount: number
  allMemberCount: number
}

export type ICorporateDetailResponse = {
  corporate: ICorporate
  report: ICorporateReport
}

export type ICorporateLocation = {
  id: number
  corporateId: number
  cityId: number
  cityName: string
  businessDistrictId: number
  businessDistrictName: string
  address: string
  longitude: number
  latitude: number
  memberCount: number
  createdAt: string
  updatedAt: string
}

export type ICorporateLocationRequest = {
  cityId: number
  businessDistrictId: number
  address: string
  longitude: number
  latitude: number
  memberCount: number
}

export type INearbyStudio = {
  id: number
  name: string
  cityName: string | null
  businessDistrictName: string | null
  address: string | null
  longitude: number
  latitude: number
  distanceKm: number
}

export type INearbyStudiosResponse = {
  corporateLocation: ICorporateLocation
  distanceKm: number
  total: number
  studios: INearbyStudio[]
}

export type PaginatedCorporatesResponse = {
  pageInfo: PageInfo
  data: ICorporateDetailResponse[]
}

export type ICorporateMembersFilterParams = {
  isActive?: boolean
  phoneNumber?: string
  expiredStartDate?: string
  expiredEndDate?: string
}

export type IPaginatedCorporateMembersParams = {
  page: number
  size: number
} & ICorporateMembersFilterParams

export type ICorporateMember = {
  id: number
  phoneNumber: string
  createdAt: string
  updatedAt: string
  isActive: boolean
  credits: number
  userCredits: number | null
  frozenCredits: number | null
  uid: string | null
  userId: number | null
  firstName: string
}

export type PaginatedCorporateMembersResponse = {
  pageInfo: PageInfo
  data: ICorporateMember[]
}

export type ICorporateMembersBulkUploadParams = {
  file: MISSING_TYPE
  phoneNumbers: string
}

export type IUpdateCorporateMembers = {
  isActive: boolean
}

export type CorporateRequest = {
  name: string
  price?: number
  allowActivityCategories?: TagActivityCategoryEnums[]
  disallowActivityList?: number[]
  maxCreditsUsageThreshold?: number
  enabledMixPayment: boolean
  studioIds?: number[]
  disallowStudioIds?: number[]
  disallowCityList?: number[]
  isActive: boolean
  creditsValidityPeriodInDays?: number
  file?: MISSING_TYPE
  unusedDistributedCreditsThreshold: number
  isTrial: boolean
  allowShowCreditsPool?: boolean
  principal?: string
  cooperationMode?: string
  contractDeadline?: string
  distributionFrequency?: string
  nextDistributionTime?: string
  distributionRuleRemark?: string
  enableReclaimCancelBooking: boolean
}

export type ICorporateCreditsHistory = {
  id: number
  type: CorporateCreditsHistoryTypeEnum
  credits: number
  notes: string
  createdAt: string
  createdBy: string
  corporateId: number
}

export type ICorporateCreditsHistoryFilterParams = {
  type?: CorporateCreditsHistoryTypeEnum
  startDate?: string
  endDate?: string
}

export type IPaginatedCorporateCreditsHistoriesParams = IPaginatedParams & ICorporatesFilterParams

export type PaginatedCorporateCreditsHistoriesResponse = {
  pageInfo: PageInfo
  data: ICorporateCreditsHistory[]
}

export type CorporateCreditsRequest = {
  credits: number
  notes?: string
  bufferType?: CorporateCreditsBufferTypeEnums
  bufferPercentage?: number
  bufferFixedValue?: number
  expiredAt?: string
}

export type CorporateUnusedDistributedCreditsRequest = {
  unusedDistributedCredits: number
  notes?: string
}

export type ReclaimCorporateCreditsRequest = {
  notes?: string
}

export type IBatchReclaimCreditsFormValue = {
  file: MISSING_TYPE
  phoneNumbers: string
  notes: string
  deactivate: boolean
  ignoreInactiveUsers?: boolean
}

export type IBatchUploadByCorporateIdResponse = {
  isSuccess: boolean
  inactivePhoneNumbers: string[] | null
  joinedOtherCorporatePhoneNumbers: string[] | null
  existingPhoneNumbers: string[] | null
  successfulPhoneNumbers: string[] | null
  notCorporateMembers: string[] | null
  corporateCreditsDistributionSchedule: string[] | null
}

export type IBatchDistributeCreditsFormValue = {
  file: MISSING_TYPE
  phoneNumbers: string
  credits: number
  notes: string
  activate?: boolean
  bufferType: CorporateCreditsBufferTypeEnums
  bufferPercentage?: number
  bufferFixedValue?: number
  expiredAt: dayjs.Dayjs
}

export type ICorporateMemberCreditsHistoryFilterParams = {
  type?: CorporateMemberCreditsHistoryTypeEnums
  referenceType?: CorporateMemberCreditsHistoryReferenceTypeEnums
  referenceId?: number
}

export type IPaginatedCorporateMemberCreditsHistoriesByCorporateMemberIdParams =
  ICorporateMemberCreditsHistoryFilterParams & IPaginatedParams

export type ICorporateMemberCreditsHistory = {
  id: number
  corporateId: number
  corporateMemberId: number
  userId: number
  credits: number
  type: CorporateMemberCreditsHistoryTypeEnums
  notes: string | null
  referenceId: number
  referenceType: CorporateMemberCreditsHistoryReferenceTypeEnums
  createdAt: string
  createdBy: string
}

export type PaginatedCorporateMemberCreditsHistoriesByCorproateMemberIdResponse = {
  pageInfo: PageInfo
  data: ICorporateMemberCreditsHistory[]
}

export type IBatchDeactiveMemberFormValue = {
  file: MISSING_TYPE
  phoneNumbers: string
  notes?: string
}

export enum CorporateCreditsDistributionScheduleType {
  SPECIFIC_TIME = 'SPECIFIC_TIME',
  REPEATABLE = 'REPEATABLE',
}

export enum IntervalUnitType {
  DAYS = 'DAYS',
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
  QUARTERS = 'QUARTERS',
  YEARS = 'YEARS',
}

export type IScheduleFormValue = {
  specificTimes?: dayjs.Dayjs[]
  type: CorporateCreditsDistributionScheduleType
  startDate?: dayjs.Dayjs
  interval?: number
  intervalUnit?: IntervalUnitType
  endDate?: dayjs.Dayjs
}

export type ICorporateCreditsDistributionScheduleFormValue = {
  file?: MISSING_TYPE
  phoneNumbers?: string
  isApplyForAllMembers: boolean
  credits: number
  bufferType: CorporateCreditsBufferTypeEnums
  bufferPercentage?: number
  bufferFixedValue?: number
  isReclaimBeforeDistribute: boolean
  isSendSms: boolean
  smsSendTime?: dayjs.Dayjs
  isActive: boolean
  schedule: IScheduleFormValue
  creditsValidityPeriod: {
    duration: string
    durationUnit: IntervalUnitType
    extendDays: number
  }
  notes: string
}

export type ISchedule = {
  specific_times?: string[]
  type: CorporateCreditsDistributionScheduleType
  start_date?: string
  interval?: number
  interval_unit?: IntervalUnitType
  end_date?: string
}

export type CorporateCreditsDistributionScheduleResponse = {
  isSuccess: boolean
  inactivePhoneNumbers?: string[]
  successfulPhoneNumbers?: string[]
  joinedOtherCorporatePhoneNumbers?: string[]
  existingPhoneNumbers?: string[]
  notCorporateMembers?: string[]
  corporateCreditsDistributionSchedule?: ICorporateCreditsDistributionSchedule
}

export type ICorporateCreditsDistributionSchedule = {
  id: number
  corporateId: number
  isActive: boolean
  credits: number
  bufferType: CorporateCreditsBufferTypeEnums
  bufferPercentage?: number
  bufferFixedValue?: number
  creditsValidityPeriod?: {
    duration: number
    duration_unit: IntervalUnitType
    extend_days: number
  }
  notes?: string
  isApplyForAllMembers: boolean
  isReclaimBeforeDistribute: boolean
  isSendSms: boolean
  smsSendTime?: number
  schedule: ISchedule
  createdAt: string
  updatedAt: string
}

export type ICorporateCreditsDistributionScheduleMember = {
  id: number
  corporateCreditsDistributionScheduleId: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  corporateMember: ICorporateMember
}

export type ICorporateCreditsDistributionScheduleHistory = {
  id: number
  corporateCreditsDistributionScheduleId: number
  status: string
  notes: string
  createdAt: string
}

export type PaginatedCorporateCreditsDistributionSchedulesResponse = {
  pageInfo: PageInfo
  data: ICorporateCreditsDistributionSchedule[]
}

export type ICorporateCreditsDistributionScheduleFilterParams = {
  isActive?: boolean
}

export type IPaginatedCorporateCreditsDistributionScheduleFilterParams = IPaginatedParams &
  ICorporateCreditsDistributionScheduleFilterParams

export type IClientCredential = {
  id: number
  clientId: string
  clientSecret: string
  name: string
  isBlock: boolean
  role: number
  createdAt: string
  updatedAt: string
}

export type ICorporateClientCredential = {
  corporate: ICorporate
  clientCredential: IClientCredential
  maxCreditsExpiredAt: string
}

export type ICorporateClientCredentialRequest = {
  corporateId: number
  clientCredentialId: number
  maxCreditsExpiredAt: string
}
